import { api } from "./api"
import Cookies from "js-cookie"

export interface LoginPayload {
  username: string
  password: string
}

export interface ConfirmPayload {
  username: string
  otp_token: string
}

export interface AuthResponse {
  username: string
  message: string
  requires_2fa?: boolean
  requires_2fa_setup?: boolean
  totp_url?: string
  secret?: string
  device_name?: string
}

export interface TokenResponse {
  access_token: string
  refresh_token: string
  user: {
    id: number
    username: string
    email: string
    first_name: string
    last_name: string
    has_2fa: boolean
  }
  message?: string
  device_name?: string
}

export interface ApiError {
  error: string
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  [key: string]: any
}

export const login = async (payload: LoginPayload): Promise<AuthResponse | TokenResponse> => {
  const { data, status } = await api.post<AuthResponse | TokenResponse>('auth/login/', payload)

  if (status < 200 && status > 300) {
    throw {
      status,
      message: "Erreur lors de la connexion"
    }
  }
  
  // Si la connexion est complète (pas de 2FA requis), stocker les tokens
  if ('access_token' in data && 'refresh_token' in data) {
    const tokenData = data as TokenResponse
    // Stocker les tokens dans les cookies
    Cookies.set('token_auth', tokenData.access_token, { expires: 7 }) // 7 jours
    Cookies.set('refresh_auth', tokenData.refresh_token, { expires: 7 })
    
    // Ne pas stocker l'objet utilisateur en cookie pour éviter des en-têtes trop volumineux
    
    // Configurer l'header Authorization pour les prochaines requêtes
    api.defaults.headers.Authorization = `Token ${tokenData.access_token}`
  }
  
  return data
}

export const confirm = async (payload: ConfirmPayload): Promise<{ data: TokenResponse, status: number }> => {
  const { data, status } = await api.post<TokenResponse>('auth/2fa/confirm/', payload)

  if (status < 200 && status > 300) {
    throw {
      status,
      message: "Erreur lors de la confirmation"
    }
  }

  return { data, status }
}

export const requestToken = async (payload: ConfirmPayload): Promise<{ data: TokenResponse, status: number }> => {
  const { data, status } = await api.post<TokenResponse>('auth/request-token/', payload)

  if (status < 200 && status > 300) {
    throw {
      status,
      message: "Erreur lors du chargement du token"
    }
  }

  return { data, status }
}

export const logout = async () => {
  try {
    const { status } = await api.post('auth/logout/', {
      refresh_token: Cookies.get('refresh_auth')
    })
    if (status < 200 && status > 300) {
      throw {
        status,
        message: "Erreur lors de la deconnexion"
      }
    }
  } catch (_e) {
    // En cas d'erreur réseau ou serveur indisponible, on continue et on nettoie les cookies
  } finally {
    Cookies.remove('token_auth')
    Cookies.remove('refresh_auth')
    Cookies.remove('user_auth')
  }
}

export type MeProfile = {
  organization: { id: number | null; name: string | null } | null
  default_media_id: number | null
  is_premium: boolean
  can_use_ai_preanalysis: boolean
  can_access_opinion: boolean
  can_access_live: boolean
  authorized_media_ids: number[]
  is_org_admin: boolean
}

export type MeResponse = {
  id: number
  username: string
  email: string
  first_name: string
  last_name: string
  is_staff?: boolean
  is_superuser?: boolean
  is_active: boolean
  date_joined: string
  has_2fa: boolean
  profile: MeProfile | null
}

export async function getMe(): Promise<MeResponse> {
  const { data } = await api.get("auth/me/")
  return data as MeResponse
}

export async function getOrgUsers(): Promise<Array<{ id:number; username:string; first_name:string; last_name:string }>> {
  const { data } = await api.get("auth/org/users/")
  return (data?.results || []) as Array<{ id:number; username:string; first_name:string; last_name:string }>
}
